<script setup lang="ts">
    import { useGlobalConfig } from '~/composables/storyblok-proxy/useGlobalConfig';
    import { normalizeCountryEntries } from '~/utils/countryConfig';
    import type { CountryListItem } from '~/utils/countryConfig';

    const { modalOpen = false } = defineProps<{
        modalOpen?: boolean;
    }>();
    const { lang, country } = useLanguage();
    const { t, te } = useI18n();

    const modalId = HEADER_LANGUAGE_PICKER_MODAL_ID;
    const languagePickerModalController = getOrCreateModalController(modalId);

    const handleOpen = () => {
        if (!languagePickerModalController.isOpen.value) {
            languagePickerModalController.open();
        }
    };

    const { data: globalCountryConfigStory } = await useGlobalConfig<CountryConfigStoryblok>('globalcountryconfig', {});

    const rawCountryEntries = computed<CountryEntryStoryblok[]>(() => {
        return globalCountryConfigStory.value?.content?.CountryList || [];
    });

    const countriesList = computed<CountryListItem[]>(() => {
        return normalizeCountryEntries(rawCountryEntries.value).map((country) => {
            const key = `languagePicker.locations.${country.key}`;

            return {
                ...country,
                name: te(key) ? t(key) : country.name
            };
        });
    });

    const countriesByCode = computed<Record<string, CountryListItem>>(() => {
        const map: Record<string, CountryListItem> = {};
        for (const country of countriesList.value) map[country.key] = country;
        return map;
    });

    const activeCountry = computed(() => {
        const entry = countriesByCode.value[country.value];
        return {
            key: country.value,
            name: entry?.name ?? t(`languagePicker.locations.${country.value}`),
            flagUrl: entry?.flagUrl,
            flagAlt: entry?.flagAlt
        };
    });
</script>

<template>
    <div
        class="languages-picker"
        :class="modalOpen ? '--modal' : ''"
    >
        <button
            @click="handleOpen"
            @keydown.space.prevent="handleOpen"
        >
            <span class="sr-only"> {{ $t('languagePicker.chosenCountry') }}: {{ activeCountry.name }} </span>
            <span class="sr-only">
                {{ $t('languagePicker.chosenLanguage') }}:
                {{ $t(`languagePicker.languages.${lang}`) }}
            </span>

            <div
                class="picker-summary"
                aria-hidden="true"
            >
                <GlobeIcon
                    v-if="activeCountry.key.toLowerCase() === 'global'"
                    aria-hidden="true"
                    class="globe-icon"
                />

                <img
                    v-else-if="activeCountry.flagUrl"
                    class="picker-flag"
                    :src="activeCountry.flagUrl"
                    :alt="activeCountry.flagAlt"
                />
                <span class="picker-country">{{ activeCountry.name }}</span>
                <span class="picker-separator">|</span>
                <span class="picker-lang">
                    {{ lang.toUpperCase() }}
                </span>
            </div>
        </button>
    </div>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/navigation/header-language-picker.scss';
</style>
